import { ViewConfig } from "@lib/views";
import { DATE_COLUMN_SIZE } from "@design-system/ViewTable/DefaultColumnSizes";
import { ResourceType } from "@common/types/auditLog";
import { HistoryField } from "@common/constants/history-source-types";
import { HistoryDetailsBaseRow } from "../../cells/historyDetailsBaseRow";
import { LogTimeCell } from "../../cells/LogTimeCell";
import {
    PreviousDateCell,
    ModifiedDateCell,
    PreviousActorCell,
    ModifiedActorCell,
    PreviousEntityListCell,
    ModifiedEntityListCell,
    PreviousDocumentListCell,
    ModifiedDocumentListCell,
    PreviousTextCell,
    ModifiedTextCell,
} from "./cells/TaskUpdateCells";
import { HistoryTaskUpdateColumn } from "./historyTaskDetailsView";

const DATE_FIELDS: ReadonlySet<HistoryField> = new Set([HistoryField.DUE_DATE]);
const ACTOR_FIELDS: ReadonlySet<HistoryField> = new Set([
    HistoryField.ASSIGNEE_ID,
]);
const ENTITY_LIST_FIELDS: ReadonlySet<HistoryField> = new Set([
    HistoryField.ENTITIES,
]);
const DOCUMENT_LIST_FIELDS: ReadonlySet<HistoryField> = new Set([
    HistoryField.FINANCIAL_DOCUMENTS,
]);

export interface HistoryTaskUpdateDetailsRow extends HistoryDetailsBaseRow {
    id: string;
    resourceType: ResourceType;
    resourceId: string;
    taskTitle: string;
    changedFieldLabel: string | null;
    changedField: HistoryField | null;
    previous: string;
    modified: string;
    previousValue: unknown;
    modifiedValue: unknown;
}

const DEFAULT_PREVIOUS_LABEL = "Previous";
const DEFAULT_MODIFIED_LABEL = "Modified";

const pickPreviousCell = (field: HistoryField | null) => {
    if (field !== null && DATE_FIELDS.has(field)) {
        return PreviousDateCell;
    }
    if (field !== null && ACTOR_FIELDS.has(field)) {
        return PreviousActorCell;
    }
    if (field !== null && ENTITY_LIST_FIELDS.has(field)) {
        return PreviousEntityListCell;
    }
    if (field !== null && DOCUMENT_LIST_FIELDS.has(field)) {
        return PreviousDocumentListCell;
    }
    return PreviousTextCell;
};

const pickModifiedCell = (field: HistoryField | null) => {
    if (field !== null && DATE_FIELDS.has(field)) {
        return ModifiedDateCell;
    }
    if (field !== null && ACTOR_FIELDS.has(field)) {
        return ModifiedActorCell;
    }
    if (field !== null && ENTITY_LIST_FIELDS.has(field)) {
        return ModifiedEntityListCell;
    }
    if (field !== null && DOCUMENT_LIST_FIELDS.has(field)) {
        return ModifiedDocumentListCell;
    }
    return ModifiedTextCell;
};

const getSingleField = (
    data: HistoryTaskUpdateDetailsRow[],
): HistoryField | null => {
    const fields = data
        .map((row) => row.changedField)
        .filter((field): field is HistoryField => field !== null);
    const unique = Array.from(new Set(fields));
    if (unique.length !== 1) {
        return null;
    }
    return unique[0];
};

const getSingleFieldLabel = (
    data: HistoryTaskUpdateDetailsRow[],
): string | null => {
    const labels = data
        .map((row) => row.changedFieldLabel)
        .filter((label): label is string => label !== null);
    const unique = Array.from(new Set(labels));
    if (unique.length !== 1) {
        return null;
    }
    return unique[0];
};

export const createHistoryTaskUpdateViewConfig = (
    data: HistoryTaskUpdateDetailsRow[],
): ViewConfig<HistoryTaskUpdateDetailsRow, HistoryTaskUpdateColumn, never> => {
    const singleFieldLabel = getSingleFieldLabel(data);
    const singleField = getSingleField(data);
    const previousLabel = singleFieldLabel
        ? `Previous ${singleFieldLabel.toLowerCase()}`
        : DEFAULT_PREVIOUS_LABEL;
    const modifiedLabel = singleFieldLabel
        ? `Modified ${singleFieldLabel.toLowerCase()}`
        : DEFAULT_MODIFIED_LABEL;

    return {
        columns: {
            [HistoryTaskUpdateColumn.TIME]: {
                name: "Time",
                header: "",
                accessorKey: "logDate",
                cell: LogTimeCell,
                ...DATE_COLUMN_SIZE,
            },
            [HistoryTaskUpdateColumn.TASK_TITLE]: {
                name: "Task",
                header: "Task",
                accessorKey: "taskTitle",
                minSize: 220,
                maxSize: 360,
            },
            [HistoryTaskUpdateColumn.PREVIOUS]: {
                name: previousLabel,
                header: previousLabel,
                accessorKey: "previous",
                cell: pickPreviousCell(singleField),
                minSize: 240,
                maxSize: 420,
            },
            [HistoryTaskUpdateColumn.MODIFIED]: {
                name: modifiedLabel,
                header: modifiedLabel,
                accessorKey: "modified",
                cell: pickModifiedCell(singleField),
                minSize: 240,
                maxSize: 420,
            },
        },
    };
};
